import Vue from 'vue';
import VueCompositionApi, { reactive } from '@vue/composition-api';
import { AudioPlayerModel } from './audio-player';
import { StoryblokImageTargetTracksLibModel } from './storyblok-image-target-lib';
import { StoryblokAudioLibModel, RecordPlayer } from './storyblok-audio-lib';

import { Stateful } from './common';

Vue.use(VueCompositionApi);

export type VinylPostcardsState = {
  version: number;
  recordPlayer: string;
  availableRecordPlayers: RecordPlayer[];
};

export type VinylPostcardsModel = Stateful<VinylPostcardsState> & {
  type: 'VinylPostcardsModel';
  changeRecordPlayer(name: string): void;
  scratchTimoutId?: number;
  stopAudioPlayerAndAddNewSongsFromImageTarget(
    imageTargetName: string,
    audioPlayer: AudioPlayerModel,
    audioLib: StoryblokAudioLibModel,
    imageTargetTracks: StoryblokImageTargetTracksLibModel
  ): void;
  stopAudioPlayerAndAddNewSongsFromUnlockCode(
    code: string,
    audioPlayer: AudioPlayerModel,
    audioLib: StoryblokAudioLibModel,
    imageTargetTracks: StoryblokImageTargetTracksLibModel
  ): boolean;
  scratchAndPlay(audioPlayer: AudioPlayerModel): void;
  setAvailableRecordPlayers(availableRecordPlayers: RecordPlayer[]): void;
};

export const DefaultVinylPostcardsState: VinylPostcardsState = {
  version: Date.now(),
  recordPlayer: 'RecordPlayerPhilips',
  availableRecordPlayers: ['RecordPlayerPhilips', 'RecordPlayerTechnics'],
};

export function createVinylPostcardsModel(state = DefaultVinylPostcardsState): VinylPostcardsModel {
  // deep copy trick with json serialization
  const _state: VinylPostcardsState = reactive(JSON.parse(JSON.stringify(state)));

  let _scratchSoundTimeoutId: undefined | number;

  const _scratchSound = document.createElement('audio');
  _scratchSound.src = '/audio/Needle_Sound_-_5.mp3';
  _scratchSound.load();

  function _invalidate() {
    _state.version = Date.now();
  }

  function changeRecordPlayer(name: string) {
    _state.recordPlayer = name;
    _invalidate();
  }

  function stopAudioPlayerAndAddNewSongsFromImageTarget(
    imageTargetName: string,
    audioPlayer: AudioPlayerModel,
    audioLib: StoryblokAudioLibModel,
    imageTargetTracks: StoryblokImageTargetTracksLibModel
  ) {
    if (_scratchSoundTimeoutId !== undefined) {
      clearTimeout(_scratchSoundTimeoutId);
      _scratchSoundTimeoutId = undefined;
    }
    _scratchSound.pause();
    _scratchSound.currentTime = 0;

    const audiotracks = imageTargetTracks.state.imageTargetTracks.find((it) => it.imageTargetName === imageTargetName)?.audioTracks;

    if (audiotracks && audiotracks.length > 0) {
      audiotracks.forEach((it) => {
        if (!audioPlayer.hasAudioTrack(it.filename)) audioPlayer.enqueueAudioTrack(it.filename, audioLib.state.audioLib);
      });
      const ids = audioPlayer.getAudioTrackIds(audiotracks[0].filename);
      if (ids.length > 0) {
        audioPlayer.stop();
        audioPlayer.setCurrentAudioTrack(ids[0]);
      }
    }
  }

  function getImageTargetFromUnlockCode(code: string, imageTargetTracks: StoryblokImageTargetTracksLibModel) {
    return imageTargetTracks.state.imageTargetTracks.find((it) => it.activationCodes?.audioTrackActivationCodes.includes(code));
  }

  function stopAudioPlayerAndAddNewSongsFromUnlockCode(
    code: string,
    audioPlayer: AudioPlayerModel,
    audioLib: StoryblokAudioLibModel,
    imageTargetTracks: StoryblokImageTargetTracksLibModel
  ) {
    const it = getImageTargetFromUnlockCode(code, imageTargetTracks);
    if (it) {
      stopAudioPlayerAndAddNewSongsFromImageTarget(it.imageTargetName, audioPlayer, audioLib, imageTargetTracks);
      return true;
    }
    return false;
  }

  function scratchAndPlay(audioPlayer: AudioPlayerModel) {
    if (_scratchSoundTimeoutId !== undefined) {
      clearTimeout(_scratchSoundTimeoutId);
      _scratchSoundTimeoutId = undefined;
    }
    _scratchSound.play();
    _scratchSoundTimeoutId = setTimeout(() => {
      if (audioPlayer.hasAudioTrack && !audioPlayer.isPlaying.value) {
        audioPlayer.togglePlay();
      }
    }, 2500);
  }

  function setAvailableRecordPlayers(availableRecordPlayers: RecordPlayer[]) {
    _state.availableRecordPlayers = availableRecordPlayers;
    _invalidate();
  }

  // initialize state - availableRecordPlayers
  if (!_state.availableRecordPlayers) {
    _state.availableRecordPlayers = DefaultVinylPostcardsState.availableRecordPlayers;
  }

  return {
    type: 'VinylPostcardsModel',
    state: _state,
    changeRecordPlayer,
    stopAudioPlayerAndAddNewSongsFromImageTarget,
    stopAudioPlayerAndAddNewSongsFromUnlockCode,
    scratchAndPlay,
    setAvailableRecordPlayers,
  };
}
